import { Phone } from 'lucide-react';
import React from 'react';
import { ContainerBox, TextInput, TypographyText } from 'src/tef/components';
import { formatPhoneDisplay } from 'src/utils/phoneFormat';

interface PhoneInputProps {
  value: string | undefined | null;
  onChange: (formatted: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  countryCode?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const extractPhoneDigits = (input: string, countryCode: string): string => {
  if (!input) return '';
  let cleaned = input.trim();
  if (cleaned.startsWith(countryCode)) {
    cleaned = cleaned.substring(countryCode.length).trimStart();
  }
  let digits = cleaned.replace(/\D/g, '');
  const ccDigits = countryCode.replace(/\D/g, '');
  if (digits.length > 10 && digits.startsWith(ccDigits)) {
    digits = digits.substring(ccDigits.length);
  }
  return digits.slice(0, 10);
};

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value,
      onChange,
      onBlur,
      countryCode = '+1',
      placeholder = '(555) 123-4567',
      disabled,
      readOnly,
      required,
      id,
      name,
      className,
      size = 'md',
    },
    ref
  ) => {
    const digits = extractPhoneDigits(value ?? '', countryCode);
    const displayValue = digits ? formatPhoneDisplay(digits) : '';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newDigits = extractPhoneDigits(e.target.value, countryCode);
      onChange(newDigits ? formatPhoneDisplay(newDigits) : '');
    };

    return (
      <ContainerBox className={`relative ${className || ''}`}>
        <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 z-10 pointer-events-none" />
        <ContainerBox className="absolute left-12 top-1/2 -translate-y-1/2 pointer-events-none z-10">
          <TypographyText className="text-slate-900 dark:text-slate-100 font-sans text-sm font-medium">
            {countryCode}
          </TypographyText>
        </ContainerBox>
        <TextInput
          ref={ref}
          type="tel"
          value={displayValue}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          variant={{ size }}
          className="pl-[4.5rem]"
          maxLength={14}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          id={id}
          name={name}
        />
      </ContainerBox>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
